import { Anchor, Boxes, Building2, CalendarClock, ClipboardCheck, ClipboardList, Cog, FileText, LayoutDashboard, Package, QrCode, ScrollText, Settings as SettingsIcon, Ship, Truck, UsersRound, Wallet, Warehouse, Wrench, X, type LucideIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { NavLink } from 'react-router-dom';
import { hasAnyRole, type AuthenticatedUser, type Role } from '@/auth/types';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';

/**
 * Single source of truth for sidebar navigation.
 *
 * Sections group nav items under translated headers. Each item
 * declares its required roles; the sidebar filters them client-side,
 * while the backend remains the canonical RBAC enforcer. A section
 * disappears entirely when no item inside is visible to the user.
 *
 * `i18nKey` resolves under `nav.*` (translated FR/EN/ES at runtime).
 */
interface NavItem {
  to: string;
  i18nKey: string;
  icon: LucideIcon;
  roles?: Role[];
}

interface NavSection {
  i18nKey: string;
  items: NavItem[];
}

const NAV_SECTIONS: NavSection[] = [
  {
    i18nKey: 'sectionMain',
    items: [
      { to: '/dashboard', i18nKey: 'dashboard', icon: LayoutDashboard },
      { to: '/scan', i18nKey: 'scan', icon: QrCode },
      { to: '/vessels', i18nKey: 'vessels', icon: Ship },
      { to: '/equipment', i18nKey: 'equipment', icon: Wrench },
      { to: '/work-orders', i18nKey: 'workOrders', icon: ClipboardList },
      { to: '/maintenance-plans', i18nKey: 'maintenancePlans', icon: CalendarClock },
      { to: '/parts', i18nKey: 'parts', icon: Package },
      { to: '/stock-lots', i18nKey: 'stock', icon: Warehouse },
      { to: '/certificates', i18nKey: 'certificates', icon: ScrollText },
      { to: '/drydock-campaigns', i18nKey: 'drydockCampaigns', icon: Anchor },
      { to: '/settings', i18nKey: 'settings', icon: SettingsIcon },
    ],
  },
  {
    i18nKey: 'sectionAdmin',
    items: [
      {
        to: '/equipment-types',
        i18nKey: 'equipmentTypes',
        icon: Cog,
        roles: ['isi_admin', 'superadmin'],
      },
      {
        to: '/part-types',
        i18nKey: 'partTypes',
        icon: Boxes,
        roles: ['isi_admin', 'superadmin'],
      },
      {
        to: '/users',
        i18nKey: 'users',
        icon: UsersRound,
        roles: ['isi_admin', 'superadmin'],
      },
      {
        to: '/companies',
        i18nKey: 'companies',
        icon: Building2,
        roles: ['isi_admin', 'superadmin'],
      },
      {
        to: '/admin/monthly-reports',
        i18nKey: 'monthlyReports',
        icon: FileText,
        // L19: admin-only cross-vessel report dashboard. Per-vessel
        // reports are reachable from the vessel detail card.
        roles: ['isi_admin', 'superadmin'],
      },
      {
        to: '/suppliers',
        i18nKey: 'suppliers',
        icon: Truck,
        // L3: fleet_manager joins the admin sidebar here because
        // they may write suppliers scoped to their own company.
        roles: ['isi_admin', 'superadmin', 'fleet_manager'],
      },
      {
        to: '/reports/budget',
        i18nKey: 'budgetReport',
        icon: Wallet,
        // L8: same gate as suppliers — fleet managers read costs
        // for their own company; admins see everything.
        roles: ['isi_admin', 'superadmin', 'fleet_manager'],
      },
      {
        to: '/inspection-templates',
        i18nKey: 'inspectionTemplates',
        icon: ClipboardCheck,
        // L14: admin / fleet author the round catalogue; the
        // execution page is reached from vessel detail.
        roles: ['isi_admin', 'superadmin', 'fleet_manager'],
      },
    ],
  },
];

interface SidebarProps {
  user: AuthenticatedUser;
  /** Mobile: controls the off-canvas drawer state. */
  open: boolean;
  onClose: () => void;
}

/**
 * Sidebar — responsive primary navigation.
 *
 * - Desktop (≥md): sticky left rail, always visible.
 * - Mobile (<md): hidden by default, slides in from the left when
 *   `open` is true. A backdrop fills the rest of the viewport so a
 *   tap outside the drawer dismisses it.
 *
 * Active link styling uses NavLink's render-prop API so the brand
 * navy highlights the current section.
 */
export function Sidebar({ user, open, onClose }: SidebarProps) {
  const { t } = useTranslation();

  const visibleSections = NAV_SECTIONS.map((section) => ({
    ...section,
    items: section.items.filter(
      (item) => !item.roles || hasAnyRole(user, item.roles),
    ),
  })).filter((section) => section.items.length > 0);

  return (
    <>
      {/* Mobile backdrop */}
      {open && (
        <button
          type="button"
          aria-label={t('common.close')}
          onClick={onClose}
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
        />
      )}

      <aside
        className={cn(
          'fixed inset-y-0 left-0 z-40 flex w-64 flex-col border-r bg-card transition-transform duration-200 md:sticky md:top-0 md:h-screen md:translate-x-0',
          open ? 'translate-x-0' : '-translate-x-full',
        )}
        aria-label="Primary navigation"
      >

        <div className="flex h-16 items-center justify-between border-b px-4">
          <span className="text-lg font-semibold text-primary">
            {t('common.appName')}
          </span>
          <Button
            type="button"
            variant="ghost"
            size="icon"
            className="md:hidden"
            onClick={onClose}
            aria-label={t('common.close')}
          >
            <X aria-hidden />
          </Button>
        </div>

        <nav className="flex-1 space-y-4 overflow-y-auto p-3">
          {visibleSections.map((section) => (
            <div key={section.i18nKey} className="space-y-1">
              <p className="px-2 pb-2 pt-1 text-xs font-semibold uppercase tracking-wider text-muted-foreground">
                {t(`nav.${section.i18nKey}`)}
              </p>
              {section.items.map((item) => {
                const Icon = item.icon;
                return (
                  <NavLink
                    key={item.to}
                    to={item.to}
                    onClick={onClose}
                    className={({ isActive }) =>
                      cn(
                        'flex items-center gap-3 rounded-md px-3 py-2 text-sm font-medium transition-colors',
                        isActive
                          ? 'bg-primary text-primary-foreground'
                          : 'text-foreground hover:bg-secondary',
                      )
                    }
                  >
                    <Icon className="h-4 w-4" aria-hidden />
                    <span>{t(`nav.${item.i18nKey}`)}</span>
                  </NavLink>
                );
              })}
            </div>
          ))}
        </nav>
      </aside>
    </>
  );
}
